import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ColumnConfig, SortEntry, ArTableStore } from "./types";

export interface ArTableStoreConfig {
  defaultColumns: ColumnConfig[];
  persistKey: string;
  fixedColumns?: Set<string>;
  stickyColumns?: Set<string>;
  defaultSorts?: SortEntry[];
  /**
   * Maps a legacy persisted column id to its current id. Lets us rename a
   * column id in code without discarding the width / visibility / position
   * already saved in existing users' browsers under the old id.
   */
  columnAliases?: Record<string, string>;
  /**
   * Schema version for the persisted column layout. Bump this number when
   * default column widths change in code and you want every user's browser
   * to pick up the new widths on next load — without losing their
   * visibility and position preferences.
   *
   * When the persisted version is older (or absent), `reconcileColumns`
   * resets widths to code defaults while keeping visibility and position.
   */
  version?: number;
}

/**
 * Reconcile a persisted column array against the current code-defined defaults.
 *
 * Column widths live in each browser's localStorage. Without reconciliation, a
 * stale or partial persisted shape (older schema, a column added/removed in
 * code, or an entry missing `width`) flows straight through to render — and a
 * single non-finite width makes the table's total width NaN, collapsing every
 * column under `table-layout: fixed`. We rebuild from `defaults` (the source of
 * truth for which columns exist + their labels/fallback widths) and overlay the
 * user's *valid* width / position / visibility by id.
 *
 * When `resetWidths` is true (triggered by a version bump), saved widths are
 * discarded so the new code-defined defaults take effect, while visibility and
 * position preferences survive.
 */
function reconcileColumns(
  persisted: ColumnConfig[] | undefined,
  defaults: ColumnConfig[],
  aliases: Record<string, string> = {},
  resetWidths = false,
): ColumnConfig[] {
  if (!Array.isArray(persisted) || persisted.length === 0) {
    return defaults.map((c) => ({ ...c }));
  }
  const byId = new Map(
    persisted.map((c) => [(c && aliases[c.id]) || c?.id, c]),
  );
  return defaults.map((def) => {
    const saved = byId.get(def.id);
    if (!saved) return { ...def };
    const width =
      !resetWidths &&
      typeof saved.width === "number" &&
      Number.isFinite(saved.width) &&
      saved.width > 0
        ? saved.width
        : def.width;
    const position =
      typeof saved.position === "number" && Number.isFinite(saved.position)
        ? saved.position
        : def.position;
    const visible =
      typeof saved.visible === "boolean" ? saved.visible : def.visible;
    return { ...def, width, position, visible };
  });
}

export function createArTableStore(config: ArTableStoreConfig) {
  const {
    defaultColumns,
    persistKey,
    fixedColumns = new Set<string>(),
    stickyColumns = new Set<string>(),
    defaultSorts = [],
    columnAliases = {},
    version = 0,
  } = config;

  return create<ArTableStore>()(
    persist(
      (set) => ({
        columns: defaultColumns,
        toggleColumn: (id) =>
          set((s) => ({
            columns: s.columns.map((c) =>
              c.id === id ? { ...c, visible: !c.visible } : c,
            ),
          })),
        reorderColumns: (columns) => set({ columns }),
        setColumnWidth: (id, width) => {
          if (fixedColumns.has(id)) return;
          set((s) => ({
            columns: s.columns.map((c) =>
              c.id === id ? { ...c, width } : c,
            ),
          }));
        },
        resetColumns: () => set({ columns: defaultColumns }),

        sorts: defaultSorts,
        setSort: (column) =>
          set((s) => {
            const idx = s.sorts.findIndex((e) => e.column === column);
            if (idx === -1)
              return {
                sorts: [...s.sorts, { column, order: "desc" }],
                page: 1,
              };
            const entry = s.sorts[idx];
            if (entry.order === "desc") {
              const next = [...s.sorts];
              next[idx] = { column, order: "asc" };
              return { sorts: next, page: 1 };
            }
            return { sorts: s.sorts.filter((_, i) => i !== idx), page: 1 };
          }),
        clearSorts: () => set({ sorts: [], page: 1 }),

        page: 1,
        setPage: (page) => set({ page }),

        fixedColumns,
        stickyColumns,
        defaultColumns,
      }),
      {
        name: persistKey,
        partialize: (state) => ({
          columns: state.columns,
          sorts: state.sorts,
          _v: version,
        }),
        merge: (persisted, current) => {
          const p = persisted as
            | { columns?: ColumnConfig[]; sorts?: SortEntry[]; _v?: number }
            | undefined;
          const savedVersion = p?._v ?? 0;
          const resetWidths = savedVersion < version;
          return {
            ...current,
            columns: reconcileColumns(
              p?.columns,
              defaultColumns,
              columnAliases,
              resetWidths,
            ),
            sorts: p?.sorts ?? current.sorts,
          };
        },
        storage: {
          getItem: (name) => {
            if (typeof window === "undefined") return null;
            const str = localStorage.getItem(name);
            if (!str) return null;
            return JSON.parse(str);
          },
          setItem: (name, value) => {
            if (typeof window === "undefined") return;
            localStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: (name) => {
            if (typeof window === "undefined") return;
            localStorage.removeItem(name);
          },
        },
      },
    ),
  );
}
